import api from '@/utils/api'
import type { LoginRequest, RegisterRequest, AuthResponse, User } from '@/types'

export const authApi = {
  // 登录
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  // 注册
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/register', userData)
    return response.data
  },

  // 微信登录
  async wechatLogin(code: string, referralCode?: string): Promise<AuthResponse> {
    const payload: any = { code }
    if (referralCode) payload.referralCode = referralCode
    const response = await api.post('/auth/wechat-login', payload)
    return response.data
  },

  async sendSmsCode(phone: string, purpose: 'register' | 'bind_phone'): Promise<{
    expiresIn: number
    resendAfter: number
    debugCode?: string
  }> {
    const response = await api.post('/auth/sms/send', { phone, purpose })
    return response.data
  },

  // 获取微信配置
  async getWechatConfig(): Promise<{ appId: string; websiteAppId?: string; supportsWechatPhoneBinding?: boolean }> {
    const response = await api.get('/auth/wechat-config')
    return response.data
  },

  // 获取当前用户信息
  async getProfile(): Promise<User> {
    const response = await api.get('/auth/profile')
    return response.data
  },

  // 刷新令牌
  async refreshToken(): Promise<AuthResponse> {
    const response = await api.post('/auth/refresh')
    return response.data
  },

  // 验证令牌
  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    const response = await api.get('/auth/verify')
    return response.data
  },

  // 生成微信扫码登录URL
  async generateWechatQRUrl(referralCode?: string): Promise<{
    sessionId: string;
    qrUrl: string;
    expiresAt: string;
  }> {
    const response = await api.get('/auth/wechat-qr-url', {
      params: referralCode ? { ref: referralCode } : undefined,
    })
    return response.data
  },

  // 检查扫码登录状态
  async checkQRLoginStatus(sessionId: string): Promise<{
    status: string;
    token?: string;
    user?: any;
  }> {
    const response = await api.get(`/auth/wechat-qr-status/${sessionId}`)
    return response.data
  },

  // 绑定手机号
  async bindPhone(phone: string, code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/bind-phone', { phone, code })
    return response.data
  },

  async bindPhoneByWechatCode(code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/bind-phone/wechat', { code })
    return response.data
  },

  // 更新个人资料
  async updateProfile(profileData: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }): Promise<User> {
    const response = await api.patch('/auth/profile', profileData)
    return response.data
  },

  // 注销（客户端清除token）
  logout(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user')
  },
}

// 微信SDK工具类
export class WechatSDK {
  private appId: string = ''
  private websiteAppId: string = ''

  async initialize() {
    try {
      const config = await authApi.getWechatConfig()
      this.appId = config.appId
      this.websiteAppId = config.websiteAppId || config.appId
      console.log('微信SDK初始化:', { appId: this.appId, websiteAppId: this.websiteAppId })
      return !!this.appId
    } catch (error) {
      console.error('Failed to initialize WeChat SDK:', error)
      return false
    }
  }

  // 检测是否为移动端
  private isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  // 获取微信授权URL（移动端使用，保持现有功能不变）
  getWechatAuthUrl(redirectUri: string, state: string = 'wechat_login'): string {
    if (!this.appId) {
      throw new Error('WeChat SDK not initialized')
    }

    // 移动端使用公众号网页授权（保持现有逻辑）
    const params = new URLSearchParams()
    params.append('appid', this.appId)
    params.append('redirect_uri', redirectUri)
    params.append('response_type', 'code')
    params.append('scope', 'snsapi_base')
    params.append('state', state)

    return `https://open.weixin.qq.com/connect/oauth2/authorize?${params.toString()}#wechat_redirect`
  }

  // 获取网站应用扫码登录URL（桌面端使用，新增功能）
  getWebsiteAuthUrl(redirectUri: string, state: string = 'website_login'): string {
    if (!this.websiteAppId) {
      throw new Error('WeChat Website SDK not initialized')
    }

    // 桌面端使用网站应用扫码登录
    const params = new URLSearchParams()
    params.append('appid', this.websiteAppId)
    params.append('redirect_uri', redirectUri)
    params.append('response_type', 'code')
    params.append('scope', 'snsapi_login')
    params.append('state', state)

    console.log('生成网站应用授权URL:', { websiteAppId: this.websiteAppId, redirectUri, state })
    return `https://open.weixin.qq.com/connect/qrconnect?${params.toString()}#wechat_redirect`
  }

  // 解析微信回调参数
  parseWechatCallback(url: string) {
    const urlObj = new URL(url)
    const code = urlObj.searchParams.get('code')
    const state = urlObj.searchParams.get('state')
    const error = urlObj.searchParams.get('error')

    return { code, state, error }
  }
}

export const wechatSDK = new WechatSDK()
